import { getApiKey } from '@/lib/api-keys';
import { callProxy, isProxyConfigured } from '@/lib/proxy';
import { tryConsume } from '@/lib/quota';

/**
 * Google Places Autocomplete (v1) + Place Details lookup.
 *
 * Calls our Supabase Edge Function proxy by default; falls back to a user-
 * provided Google key if they've set one in Settings → Admin → API keys.
 * Returns a small typed shape with just the fields we need to fill the
 * property form: full address line, ZIP, city, state, lat/lon.
 */

export interface PlaceSuggestion {
  place_id: string;
  primary: string;
  secondary: string;
}

export interface PlaceDetail {
  address: string;
  zip: string | null;
  city: string | null;
  state: string | null;
  lat: number | null;
  lon: number | null;
}

const AUTOCOMPLETE_URL = 'https://places.googleapis.com/v1/places:autocomplete';
const DETAILS_URL = 'https://places.googleapis.com/v1/places';

interface AutocompleteResponse {
  suggestions?: Array<{
    placePrediction?: {
      placeId?: string;
      structuredFormat?: {
        mainText?: { text?: string };
        secondaryText?: { text?: string };
      };
      text?: { text?: string };
    };
  }>;
}

interface DetailsResponse {
  formattedAddress?: string;
  location?: { latitude?: number; longitude?: number };
  addressComponents?: Array<{
    types?: string[];
    longText?: string;
    shortText?: string;
  }>;
}

export type PlacesResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: { kind: 'no-key' | 'network' | 'places'; message: string } };

export async function autocompletePlaces(
  input: string
): Promise<PlacesResult<PlaceSuggestion[]>> {
  const trimmed = input.trim();
  if (trimmed.length < 3) return { ok: true, data: [] };
  const userKey = await getApiKey('google_places');
  if (!userKey && !isProxyConfigured()) {
    return { ok: false, error: { kind: 'no-key', message: 'No Google Places API key' } };
  }
  const budget = await tryConsume('places');
  if (!budget.ok) {
    return {
      ok: false,
      error: {
        kind: 'places',
        message: `Daily Places budget hit (${budget.ceiling}). Resets at midnight UTC.`,
      },
    };
  }
  try {
    let payload: AutocompleteResponse;
    if (userKey) {
      const res = await fetch(AUTOCOMPLETE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Goog-Api-Key': userKey },
        body: JSON.stringify({ input: trimmed, includedRegionCodes: ['us'] }),
      });
      const raw = (await res.json()) as AutocompleteResponse | Record<string, unknown>;
      if (!res.ok) {
        const msg = (raw as { error?: { message?: string } }).error?.message ?? `HTTP ${res.status}`;
        return { ok: false, error: { kind: 'places', message: msg } };
      }
      payload = raw as AutocompleteResponse;
    } else {
      const proxied = await callProxy<AutocompleteResponse>({
        service: 'places-autocomplete',
        input: trimmed,
      });
      if (!proxied.ok) {
        const kind = proxied.error.kind === 'network' ? 'network' : 'places';
        return { ok: false, error: { kind, message: proxied.error.message } };
      }
      payload = proxied.payload;
    }
    const suggestions = payload.suggestions ?? [];
    const out: PlaceSuggestion[] = [];
    for (const s of suggestions) {
      const pp = s.placePrediction;
      if (!pp?.placeId) continue;
      out.push({
        place_id: pp.placeId,
        primary: pp.structuredFormat?.mainText?.text ?? pp.text?.text ?? '',
        secondary: pp.structuredFormat?.secondaryText?.text ?? '',
      });
    }
    return { ok: true, data: out };
  } catch (err) {
    return { ok: false, error: { kind: 'network', message: String(err) } };
  }
}

export async function getPlaceDetails(
  placeId: string
): Promise<PlacesResult<PlaceDetail>> {
  const userKey = await getApiKey('google_places');
  if (!userKey && !isProxyConfigured()) {
    return { ok: false, error: { kind: 'no-key', message: 'No Google Places API key' } };
  }
  try {
    let p: DetailsResponse;
    if (userKey) {
      const res = await fetch(`${DETAILS_URL}/${encodeURIComponent(placeId)}`, {
        method: 'GET',
        headers: {
          'X-Goog-Api-Key': userKey,
          'X-Goog-FieldMask': 'formattedAddress,location,addressComponents',
        },
      });
      const raw = (await res.json()) as DetailsResponse | Record<string, unknown>;
      if (!res.ok) {
        const msg = (raw as { error?: { message?: string } }).error?.message ?? `HTTP ${res.status}`;
        return { ok: false, error: { kind: 'places', message: msg } };
      }
      p = raw as DetailsResponse;
    } else {
      const proxied = await callProxy<DetailsResponse>({
        service: 'places-details',
        placeId,
      });
      if (!proxied.ok) {
        const kind = proxied.error.kind === 'network' ? 'network' : 'places';
        return { ok: false, error: { kind, message: proxied.error.message } };
      }
      p = proxied.payload;
    }
    const comps = p.addressComponents ?? [];
    const findComp = (...wanted: string[]) =>
      comps.find((c) => c.types?.some((t) => wanted.includes(t)));
    return {
      ok: true,
      data: {
        address: p.formattedAddress ?? '',
        zip: findComp('postal_code')?.longText ?? null,
        city: findComp('locality', 'postal_town', 'sublocality')?.longText ?? null,
        state: findComp('administrative_area_level_1')?.shortText ?? null,
        lat: p.location?.latitude ?? null,
        lon: p.location?.longitude ?? null,
      },
    };
  } catch (err) {
    return { ok: false, error: { kind: 'network', message: String(err) } };
  }
}
